import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { GUEST_ID_BUCKET } from '@/hooks/useGuestPhotoUpload';

// Types
export interface GuestVerification {
  id: string;
  booking_id: string;
  is_primary: boolean;
  first_name: string;
  last_name: string;
  full_name: string;
  gender: 'M' | 'F' | null;
  date_of_birth: string;
  place_of_birth?: string;
  country_of_birth?: string;
  nationality: string;
  country_of_residence?: string;
  city_of_residence?: string;
  document_type: 'id_card' | 'passport' | 'other' | null;
  document_number?: string;
  document_expiry?: string;
  document_issuing_country?: string;
  parent_verification_id?: string;
  is_child: boolean;
  verification_method: 'manual' | 'in_person';
  verification_status: 'pending' | 'verified' | 'failed' | 'skipped';
  verified_at?: string;
  document_photo_url?: string;
  selfie_url?: string;
  created_at: string;
  updated_at: string;
}

export interface GuestVerificationInput {
  booking_id: string;
  is_primary?: boolean;
  first_name: string;
  last_name: string;
  gender?: 'M' | 'F';
  date_of_birth: string;
  place_of_birth?: string;
  country_of_birth?: string;
  nationality: string;
  country_of_residence?: string;
  city_of_residence?: string;
  document_type?: 'id_card' | 'passport' | 'other';
  document_number?: string;
  document_expiry?: string;
  document_issuing_country?: string;
  parent_verification_id?: string;
  is_child?: boolean;
  verification_method: 'manual' | 'in_person';
  document_photo_url?: string;
  selfie_url?: string;
}

// Hook za dohvat svih gostiju na rezervaciji
export function useBookingGuests(bookingId: string | undefined) {
  return useQuery({
    queryKey: ['booking-guests', bookingId],
    queryFn: async () => {
      if (!bookingId) return [];

      const { data, error } = await supabase
        .from('guest_verifications')
        .select('*')
        .eq('booking_id', bookingId)
        .order('is_primary', { ascending: false })
        .order('is_child', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return withSignedPhotoUrls(data as GuestVerification[]);
    },
    enabled: !!bookingId,
  });
}

// ID photos live in the private guest-ids bucket as paths; the landlord sees
// them through short-lived signed URLs (legacy rows hold full URLs).
const PHOTO_URL_TTL_SECONDS = 6 * 60 * 60;

async function withSignedPhotoUrls(guests: GuestVerification[]): Promise<GuestVerification[]> {
  const paths = guests
    .flatMap((g) => [g.document_photo_url, g.selfie_url])
    .filter((p): p is string => !!p && !p.startsWith('http'));
  if (paths.length === 0) return guests;

  const { data, error } = await supabase.storage.from(GUEST_ID_BUCKET).createSignedUrls(paths, PHOTO_URL_TTL_SECONDS);
  if (error) console.error('Could not sign guest ID photo URLs:', error);
  const signed = new Map<string, string>(
    (data ?? []).filter((d) => d.path && d.signedUrl).map((d) => [d.path as string, d.signedUrl]),
  );
  const resolve = (p?: string) => (p && !p.startsWith('http') ? signed.get(p) : p);

  return guests.map((g) => ({
    ...g,
    document_photo_url: resolve(g.document_photo_url),
    selfie_url: resolve(g.selfie_url),
  }));
}

// Hook za dodavanje gosta
export function useAddGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GuestVerificationInput) => {
      const { data, error } = await supabase
        .from('guest_verifications')
        .insert({
          ...input,
          verification_status: input.verification_method === 'manual' ? 'verified' : 'pending',
          verified_at: input.verification_method === 'manual' ? new Date().toISOString() : null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['booking-guests', data.booking_id] });
    },
  });
}

// Hook za ažuriranje gosta
export function useUpdateGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<GuestVerification> & { id: string }) => {
      const { data, error } = await supabase
        .from('guest_verifications')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['booking-guests', data.booking_id] });
    },
  });
}

// Hook za brisanje gosta
export function useDeleteGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, bookingId }: { id: string; bookingId: string }) => {
      const { error } = await supabase
        .from('guest_verifications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { bookingId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['booking-guests', data.bookingId] });
    },
  });
}

// Hook za provjeru je li check-in završen
export function useCheckinStatus(bookingId: string | undefined) {
  return useQuery({
    queryKey: ['checkin-status', bookingId],
    queryFn: async () => {
      if (!bookingId) return { complete: false, verified: 0, total: 0 };

      // Dohvati booking i unit
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          id,
          adults_count,
          children_count,
          infants_count,
          checkin_completed,
          unit:units(verification_mode)
        `)
        .eq('id', bookingId)
        .single();

      if (bookingError) throw bookingError;

      const verificationMode = (booking.unit as { verification_mode?: string } | null)?.verification_mode || 'in_person';

      // Ako je "na licu mjesta", uvijek je OK
      if (verificationMode === 'in_person') {
        return { complete: true, verified: 0, total: 0, mode: verificationMode };
      }

      const totalGuests = (booking.adults_count || 0) + (booking.children_count || 0);

      // Dohvati broj verificiranih gostiju
      const { data: guests, error: guestsError } = await supabase
        .from('guest_verifications')
        .select('id, verification_status')
        .eq('booking_id', bookingId)
        .in('verification_status', ['verified', 'skipped']);

      if (guestsError) throw guestsError;

      const verifiedCount = guests?.length || 0;

      return {
        complete: verifiedCount >= totalGuests,
        verified: verifiedCount,
        total: totalGuests,
        mode: verificationMode,
      };
    },
    enabled: !!bookingId,
  });
}

// Hook za export podataka za eVisitor
export function useExportEvisitor(bookingId: string | undefined) {
  const exportData = async () => {
    if (!bookingId) return;

    try {
      const { data, error } = await supabase.rpc('export_for_evisitor', {
        p_booking_id: bookingId,
      });

      if (error) throw error;

      // Kreiraj CSV
      const headers = [
        'Ime',
        'Prezime',
        'Spol',
        'Datum rođenja',
        'Mjesto rođenja',
        'Država rođenja',
        'Državljanstvo',
        'Država boravka',
        'Grad boravka',
        'Tip dokumenta',
        'Broj dokumenta',
      ];

      interface EVisitorRow {
        ime: string;
        prezime: string;
        spol: string;
        datum_rodjenja: string;
        mjesto_rodjenja: string;
        drzava_rodjenja: string;
        drzavljanstvo: string;
        drzava_boravka: string;
        grad_boravka: string;
        tip_dokumenta: string;
        broj_dokumenta: string;
      }
      const rows = data.map((row: EVisitorRow) => [
        row.ime,
        row.prezime,
        row.spol,
        row.datum_rodjenja,
        row.mjesto_rodjenja,
        row.drzava_rodjenja,
        row.drzavljanstvo,
        row.drzava_boravka,
        row.grad_boravka,
        row.tip_dokumenta,
        row.broj_dokumenta,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell: string) => `"${cell || ''}"`).join(','))
        .join('\n');

      // Download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evisitor-export-${bookingId.slice(0, 8)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export error:', error);
    }
  };

  return { exportData };
}

// ─── Guest Portal (anon-safe) hooks ───

// Hook za dohvat gostiju putem access tokena (za Guest Portal)
export function useBookingGuestsByToken(accessToken: string | undefined) {
  return useQuery({
    queryKey: ['booking-guests-token', accessToken],
    queryFn: async () => {
      if (!accessToken) return [];

      const { data, error } = await supabase.rpc('get_booking_guests_by_token', {
        p_access_token: accessToken,
      });

      if (error) throw error;
      return (data || []) as GuestVerification[];
    },
    enabled: !!accessToken,
  });
}

// Hook za dodavanje gosta putem access tokena (za Guest Portal)
export function useAddGuestByToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accessToken,
      ...input
    }: Omit<GuestVerificationInput, 'booking_id'> & { accessToken: string }) => {
      const { data, error } = await supabase.rpc('add_guest_verification', {
        p_access_token: accessToken,
        p_first_name: input.first_name,
        p_last_name: input.last_name,
        p_date_of_birth: input.date_of_birth,
        p_nationality: input.nationality,
        p_is_primary: input.is_primary || false,
        p_gender: input.gender || null,
        p_place_of_birth: input.place_of_birth || null,
        p_country_of_birth: input.country_of_birth || null,
        p_country_of_residence: input.country_of_residence || null,
        p_city_of_residence: input.city_of_residence || null,
        p_document_type: input.document_type || null,
        p_document_number: input.document_number || null,
        p_document_expiry: input.document_expiry || null,
        p_document_issuing_country: input.document_issuing_country || null,
        p_parent_verification_id: input.parent_verification_id || null,
        p_is_child: input.is_child || false,
        p_verification_method: input.verification_method,
        p_document_photo_url: input.document_photo_url || null,
        p_selfie_url: input.selfie_url || null,
      });

      if (error) throw error;
      // The RPC answers {error: …} (invalid link, photo path, parent) instead
      // of raising — the form must not close as if it had been saved.
      if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['booking-guests-token', variables.accessToken] });
    },
  });
}

// Hook za brisanje gosta putem access tokena (za Guest Portal)
export function useDeleteGuestByToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accessToken, guestId }: { accessToken: string; guestId: string }) => {
      const { data, error } = await supabase.rpc('delete_guest_verification_by_token', {
        p_access_token: accessToken,
        p_guest_id: guestId,
      });

      if (error) throw error;
      return { accessToken, success: data };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['booking-guests-token', data.accessToken] });
    },
  });
}

// Lista zemalja za dropdown
export const countries = [
  { code: 'HR', name: 'Hrvatska', nameEn: 'Croatia' },
  { code: 'DE', name: 'Njemačka', nameEn: 'Germany' },
  { code: 'AT', name: 'Austrija', nameEn: 'Austria' },
  { code: 'IT', name: 'Italija', nameEn: 'Italy' },
  { code: 'SI', name: 'Slovenija', nameEn: 'Slovenia' },
  { code: 'HU', name: 'Mađarska', nameEn: 'Hungary' },
  { code: 'CZ', name: 'Češka', nameEn: 'Czech Republic' },
  { code: 'SK', name: 'Slovačka', nameEn: 'Slovakia' },
  { code: 'PL', name: 'Poljska', nameEn: 'Poland' },
  { code: 'FR', name: 'Francuska', nameEn: 'France' },
  { code: 'GB', name: 'Velika Britanija', nameEn: 'United Kingdom' },
  { code: 'NL', name: 'Nizozemska', nameEn: 'Netherlands' },
  { code: 'BE', name: 'Belgija', nameEn: 'Belgium' },
  { code: 'CH', name: 'Švicarska', nameEn: 'Switzerland' },
  { code: 'ES', name: 'Španjolska', nameEn: 'Spain' },
  { code: 'PT', name: 'Portugal', nameEn: 'Portugal' },
  { code: 'SE', name: 'Švedska', nameEn: 'Sweden' },
  { code: 'NO', name: 'Norveška', nameEn: 'Norway' },
  { code: 'DK', name: 'Danska', nameEn: 'Denmark' },
  { code: 'FI', name: 'Finska', nameEn: 'Finland' },
  { code: 'RU', name: 'Rusija', nameEn: 'Russia' },
  { code: 'UA', name: 'Ukrajina', nameEn: 'Ukraine' },
  { code: 'RS', name: 'Srbija', nameEn: 'Serbia' },
  { code: 'BA', name: 'Bosna i Hercegovina', nameEn: 'Bosnia and Herzegovina' },
  { code: 'ME', name: 'Crna Gora', nameEn: 'Montenegro' },
  { code: 'MK', name: 'Sjeverna Makedonija', nameEn: 'North Macedonia' },
  { code: 'AL', name: 'Albanija', nameEn: 'Albania' },
  { code: 'RO', name: 'Rumunjska', nameEn: 'Romania' },
  { code: 'BG', name: 'Bugarska', nameEn: 'Bulgaria' },
  { code: 'GR', name: 'Grčka', nameEn: 'Greece' },
  { code: 'TR', name: 'Turska', nameEn: 'Turkey' },
  { code: 'US', name: 'SAD', nameEn: 'United States' },
  { code: 'CA', name: 'Kanada', nameEn: 'Canada' },
  { code: 'AU', name: 'Australija', nameEn: 'Australia' },
  { code: 'JP', name: 'Japan', nameEn: 'Japan' },
  { code: 'CN', name: 'Kina', nameEn: 'China' },
  { code: 'KR', name: 'Južna Koreja', nameEn: 'South Korea' },
  { code: 'IL', name: 'Izrael', nameEn: 'Israel' },
  { code: 'BR', name: 'Brazil', nameEn: 'Brazil' },
  { code: 'AR', name: 'Argentina', nameEn: 'Argentina' },
];

// Sortiraj s Hrvatskom na vrhu
export const sortedCountries = [
  countries.find((c) => c.code === 'HR')!,
  ...countries.filter((c) => c.code !== 'HR').sort((a, b) => a.name.localeCompare(b.name, 'hr')),
];

/**
 * Countries sorted for a country picker: Croatia pinned on top (current user
 * base), the rest alphabetical in the UI language. Use this for the property
 * country dropdown so the list reads naturally in HR and EN.
 */
export function getSortedCountries(lang: string) {
  const en = lang.startsWith('en');
  const label = (c: { name: string; nameEn: string }) => (en ? c.nameEn : c.name);
  const hr = countries.find((c) => c.code === 'HR')!;
  const rest = countries
    .filter((c) => c.code !== 'HR')
    .sort((a, b) => label(a).localeCompare(label(b), en ? 'en' : 'hr'));
  return [hr, ...rest];
}

/**
 * The canonical value stored in `properties.country` is the ENGLISH name
 * (e.g. "Croatia") — stable and locale-independent. This turns it back into a
 * label for display in the current UI language. Falls back to the stored
 * string for legacy rows that don't match the list (older free-text entries).
 */
export function getCountryLabel(stored: string | null | undefined, lang: string): string {
  if (!stored) return '';
  const en = lang.startsWith('en');
  const match = countries.find(
    (c) => c.nameEn === stored || c.name === stored || c.code === stored,
  );
  if (!match) return stored;
  return en ? match.nameEn : match.name;
}

/**
 * Normalize any country string (Croatian name, English name, or ISO code) to
 * the canonical English name so legacy free-text rows line up with the picker.
 * Returns the input unchanged if it isn't recognized.
 */
export function toCanonicalCountry(stored: string | null | undefined): string {
  if (!stored) return '';
  const match = countries.find(
    (c) => c.nameEn === stored || c.name === stored || c.code === stored,
  );
  return match ? match.nameEn : stored;
}
